import { create } from 'zustand';
import { api } from '../services/api';

export interface SpecialDay {
  id: string;
  family_id: string;
  title: string;
  date: string;
  recurring: boolean;
  category: string;
  notes: string;
  notify_days_before: number;
  member_id: string;
  gift_budget_pence?: number | null;
  // Private fields, only used to power tailored gift suggestions. Stored on
  // the special_day so they age with the year. Never rendered as labels.
  person_birth_year?: number | null;
  person_gender?: string | null;
  created_by: string;
  created_at: string;
}

interface SpecialDaysState {
  specialDays: SpecialDay[];
  upcoming: SpecialDay[];
  isLoading: boolean;
  error: string | null;
  fetchSpecialDays: (category?: string) => Promise<void>;
  fetchUpcoming: () => Promise<void>;
  createSpecialDay: (data: Partial<SpecialDay>) => Promise<SpecialDay>;
  updateSpecialDay: (specialDayId: string, data: Partial<SpecialDay>) => Promise<void>;
  deleteSpecialDay: (specialDayId: string) => Promise<void>;
  handleWsMessage: (message: any) => void;
}

export const useSpecialDaysStore = create<SpecialDaysState>((set, get) => ({
  specialDays: [],
  upcoming: [],
  isLoading: false,
  error: null,

  fetchSpecialDays: async (category?) => {
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      const query = params.toString() ? `?${params.toString()}` : '';
      const res = await api.get(`/special-days${query}`);
      set({ specialDays: res.data.special_days || res.data.specialDays, isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  fetchUpcoming: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get('/special-days/upcoming');
      set({ upcoming: res.data.special_days || res.data.upcoming, isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  createSpecialDay: async (data) => {
    try {
      const res = await api.post('/special-days', data);
      const newSpecialDay = res.data.special_day || res.data.specialDay;
      set(state => ({ specialDays: [newSpecialDay, ...state.specialDays] }));
      return newSpecialDay;
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  updateSpecialDay: async (specialDayId, data) => {
    try {
      const res = await api.put(`/special-days/${specialDayId}`, data);
      const updatedSpecialDay = res.data.special_day || res.data.specialDay;
      set(state => ({
        specialDays: state.specialDays.map(s => s.id === specialDayId ? updatedSpecialDay : s),
        upcoming: state.upcoming.map(s => s.id === specialDayId ? updatedSpecialDay : s),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  deleteSpecialDay: async (specialDayId) => {
    try {
      await api.delete(`/special-days/${specialDayId}`);
      set(state => ({
        specialDays: state.specialDays.filter(s => s.id !== specialDayId),
        upcoming: state.upcoming.filter(s => s.id !== specialDayId),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  handleWsMessage: (message) => {
    switch (message.type) {
      case 'SPECIAL_DAY_CREATED':
        set(state => {
          const exists = state.specialDays.find(s => s.id === message.payload.id);
          if (exists) return state;
          return { specialDays: [message.payload, ...state.specialDays] };
        });
        break;

      case 'SPECIAL_DAY_UPDATED':
        set(state => ({
          specialDays: state.specialDays.map(s =>
            s.id === message.payload.id ? { ...s, ...message.payload } : s
          ),
          upcoming: state.upcoming.map(s =>
            s.id === message.payload.id ? { ...s, ...message.payload } : s
          ),
        }));
        break;

      case 'SPECIAL_DAY_DELETED':
        set(state => ({
          specialDays: state.specialDays.filter(s => s.id !== message.payload.id),
          upcoming: state.upcoming.filter(s => s.id !== message.payload.id),
        }));
        break;
    }
  },
}));
